/* <------------------------> */
//#region IMPORTS
/* <------------------------> */
import React, { useState, useEffect, useRef } from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { Alert, Card, Modal, Nav, Button } from 'react-bootstrap';

import AcknowledgmentField from "../components/AcknowledgmentField";
import ADSearchField from "../components/ADSearchField";
import ApproveRejectField from "../components/ApproveRejectField";
import ButtonType from '../components/ButtonType';
import DateField from '../components/DateField';
import EngineeringReview from "../components/EngineeringReview";
import FileField from "../components/FileField";
import LoadingWrapper from "../components/LoadingWrapper";
import MediaField from '../components/MediaField';
import { MultiSelectOption } from "../components/MultiSelectField";
import NumberField from '../components/NumberField';
import QualityExpediting from "../components/QualityExpediting";
import SearchField from "../components/SearchField";
import SelectField from '../components/SelectField';
import SectionWrapper from "../components/SectionWrapper";
import SupplyChainReview from "../components/SupplyChainReview";
import TaskDiscussion from "../components/TaskDiscussion";
import TaskHistory from "../components/TaskHistory";
import TextArea from "../components/TextArea";
import TextField from '../components/TextField';
import { infoToast } from '../components/Toasty';

import * as NCRDataConstants from "../constants/NCRDataConstants";

import { FieldType } from '../enums/FieldType';
import { Stage, StageLabel } from '../enums/NCR';

import FormatNumber from "../functions/FormatNumber";

import DataService from "../services/DataService";
import TaskService from "../services/TaskService";

import IEmployeeDetail from '../types/Employee/EmployeeDetail';
import EmployeeInfo from "../types/Employee/EmployeeInfo";
import IFileUpload from '../types/FileUpload'
import IScreenFieldUpdate from '../types/Screen/ScreenFieldUpdate';
import ITask from '../types/Task/Task';
import ITaskAssign from '../types/Task/TaskAssign'
import ITaskComment from "../types/Task/TaskComment";
import ITaskDelete from '../types/Task/TaskDelete'
import ITaskHistory from "../types/Task/TaskHistory";

/* <------------------------> */
//#endregion
/* <------------------------> */

/* <------------------------> */
//#region PROPS AND TYPES
/* <------------------------> */

type Props = {
  currentUser: EmployeeInfo;
  isMocked: boolean;
};

type ReassignOption = {
  value: string;
  label: string;
};

type SelectOption = {
  label: string;
  value: string;
};

const overrideJobCodes: string[] = [
  "BQU351",   // Quality Systems Analyst
  "BQU400",   // Senior Quality Systems Analyst
  "BQU100"    // Quality Specialist
];

// Initial task state
const initialTask: ITask = {
  id: 0,
  ncrID: "",
  ncrType: "",
  projectNumber: "",
  projectName: "",
  location: "",
  description: "",
  dueDate: "",
  initialCostEsitmate: "",
  assignedTo: "",
  assignedToName: "",
  completed: false,
  groupUsers: [],
  stage: Stage.Unknown,
  tier: 0,
  screen: {
    name: "",
    screenFields: [
      {
        dataName: "",
        value: "",
        type: FieldType.None,
        title: "",
        hintText: "",
        buttonText: "",
        buttonColor: "",
        headerColor: "",
        textColor: "",
        time: false,
        readOnly: true,
        required: false,
        options: []
      }
    ]
  }
};

/* <------------------------> */
//#endregion
/* <------------------------> */

const TaskDetailsPage: React.FC<Props> = ({ currentUser, isMocked }) => {
  // Router hooks
  const { query: taskId } = useParams<{ query: string }>();
  const location = useLocation();
  const history = useHistory();
  const userJobCode = (location.state as string) || '';

  // Refs
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

/* <------------------------> */
//#region STATE
/* <------------------------> */

  // State hooks
  const [selectOptionsReassign, setSelectOptionsReassign] = useState<ReassignOption[]>([]);
  const [singleOptionsReassign, setSingleOptionsReassign] = useState("");
  const [backToTaskPage, setBackToTaskPage] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openReassignModal, setOpenReassignModal] = useState(false);
  const [loadingComplete, setLoadingComplete] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [fileUpload, setFileUpload] = useState<IFileUpload>({ files: [], dataName: "" });
  const [currentTask, setCurrentTask] = useState<ITask>(initialTask);
  const [qualityExpeditingStage, setQualityExpeditingStage] = useState<Stage>(Stage.Unknown);
  const [taskComments, setTaskComments] = useState<ITaskComment[]>([]);
  const [taskCommentsLoaded, setTaskCommentsLoaded] = useState(false);
  const [taskHistory, setTaskHistory] = useState<ITaskHistory[]>([]);
  const [tab, setTab] = useState("NCR");
  const [submitButton, setSubmitButton] = useState("");
  const [approval, setApproval] = useState<string | undefined>();
  const [rejectTo, setRejectTo] = useState<string | undefined>();
  const [dcAcknowledgment, setDcAcknowledgment] = useState(false);
  const [error, setError] = useState(false);
  const [formError, setFormError] = useState(false);
  const [workCodeOptions, setWorkCodeOptions] = useState<MultiSelectOption[]>([]);
  const [defectFailureModesOptions, setDefectFailureModesOptions] = useState<SelectOption[]>([]);
  const [subcontractorNameOptions, setSubcontractorNameOptions] = useState<SelectOption[]>([]);
  const [supplierVendorNameOptions, setSupplierVendorNameOptions] = useState<SelectOption[]>([]);

/* <------------------------> */
//#endregion
/* <------------------------> */


/* <------------------------> */
//#region CREATING, REASSIGNING, OR DELETING A TASK
/* <------------------------> */

  // Helper function to remove assignee from list when task is claimed
  const removeAssigneeFromList = (array: ReassignOption[], value: string): ReassignOption[] => {
    const idx = array.findIndex((x) => x.value === value);
    if (idx !== -1) {
      const newArray = [...array];
      newArray.splice(idx, 1);
      return newArray;
    }
    return array;
  };

  // Function to claim task
  const claimTask = async (userEmail: string, id: number) => {
    if (userEmail !== "") {
      // if we are logged in as a different user, do not allow the Reassign/Claim actions to happen
      if (isMocked) {
        let msg = 'You have clicked on the "';
        if (userEmail !== currentUser.email) {
          msg += 'Reassign';
        } else {
          msg += 'Claim';
        }
        msg += '" button.  This has been disabled because you are currently not yourself.';
        // send toast message here
        infoToast(msg);
        // close modal
        setOpenReassignModal(false);
        return;
      }

      setLoadingComplete(false);
      setLoadingMessage(userEmail === currentUser.email ? "Claiming NCR Task..." : "Reassigning NCR Task...");
      setOpenReassignModal(false);

      const data: ITaskAssign = {
        id: id,
        user: currentUser.email,
        assignedTo: userEmail
      };

      await TaskService.updateTask(data)
        .then((response) => {
          if (userEmail !== currentUser.email) {
            if (saveButtonRef.current) {
              saveButtonRef.current.click();
            }
            setLoadingComplete(true);
            setBackToTaskPage(true);
          } else if (response.status === 200) {
            const task = { ...currentTask };
            task.assignedTo = userEmail;
            setLoadingComplete(true);
            setCurrentTask(task);
            setSelectOptionsReassign(removeAssigneeFromList(selectOptionsReassign, userEmail));
          } else {
            setBackToTaskPage(true);
          }
        }).catch((e) => {
          setLoadingComplete(true);
          setError(true);
        });
    }
  };

  // Function to delete task
  const deleteTask = async (id: number, reason: string) => {
    if (isMocked) {
      // if we are logged in as a different user, do not allow the delete actions to happen
      infoToast('You have clicked on the "Delete" button.  This has been disabled because you are currently not yourself.');
      setOpenDeleteModal(false);
      return;
    }

    if (reason.length > 1) {
      setLoadingComplete(false);
      setLoadingMessage("Deleting NCR...");
      setOpenDeleteModal(false);

      const data: ITaskDelete = {
        id: id,
        deleteReason: reason,
        deletedByUserEmail: currentUser.email
      };

      await TaskService.deleteTask(data)
        .then((response) => {
          if (response.status === 200) {
            setBackToTaskPage(true);
          }
        }).catch((e) => {
          setLoadingComplete(true);
          setError(true);
        });
    }
  };

/* <------------------------> */
//#endregion
/* <------------------------> */


/* <------------------------> */
//#region GET TASK, COMMENTS, AND HISTORY DATA
/* <------------------------> */

  // Function to get a field's value
  const getValue = (dataName: string): string => {
    const result = currentTask.screen.screenFields.find(x => x.dataName === dataName)?.value;
    return result ? result : "";
  };

  // Function to set a field's value
  const setValue = (dataName: string, value: string) => {
    setCurrentTask(prevTask => {
      const newTask = { ...prevTask };
      const fieldIndex = newTask.screen.screenFields.findIndex(x => x.dataName === dataName);

      if (fieldIndex !== -1) {
        newTask.screen.screenFields = [...newTask.screen.screenFields];
        newTask.screen.screenFields[fieldIndex] = { ...newTask.screen.screenFields[fieldIndex], value };
      } else {
        newTask.screen.screenFields = [...newTask.screen.screenFields, {
          dataName: dataName,
          value: value,
          type: FieldType.Hidden,
          title: "",
          hintText: "",
          buttonText: "",
          buttonColor: "",
          headerColor: "",
          textColor: "",
          time: false,
          readOnly: false,
          required: false,
          options: []
        }];
      }
      return newTask;
    });
  };

  // Function to handle tier change
  const handleTierChange = () => {
    const combinedTotalCost = parseFloat(getValue(NCRDataConstants.COMBINED_TOTAL_COST));
    if (combinedTotalCost >= 25000) {
      setCurrentTask(prevTask => ({ ...prevTask, tier: 3 }));
    }
  };

  // Function to handle type change
  const handleTypeChange = () => {
    const newType = getValue("NonconformanceType");
    setCurrentTask(prevTask => ({ ...prevTask, ncrType: newType }));
  };

  // Function to get options for specific select fields
  const getSelectFieldOptions = async (task: ITask) => {
    // Set queries
    const workCodeQuery = {
      query: [{
        column: 1,
        values: [task.projectNumber]
      }]
    };
    const subcontractorNameQuery = {
      query: [{
        column: 2,
        values: ["SUB"]
      }]
    };
    const supplierVendorNameQuery = {
      query: [{
        column: 2,
        values: ["C", "ENG", "EQP", "M", "MSC", "P", "R", "RNT", "ROY", "SAF", "SMP", "SMS", "STL", "SUB", "SVC", "T", "TIR"]
      }]
    };

    // Get Work Code options
    setLoadingMessage("Syncing Work Code Data from Blattner Mobile...");

    // check if the form entry used Enterprise Data
    const isEnterprise = task.screen.screenFields.find(x => x.dataName === 'JDESource')?.value === 'Enterprise';

    // Get work code data
    try {
      const targetDS = isEnterprise ? "BOOMI_WORKCODES_ENTERPRISE" : "BOOMI_WORKCODES";
      const workCodeData = (await DataService.searchDataSourceDataWithQuery(targetDS, 4, workCodeQuery)).data;

      if (workCodeData) {
        setWorkCodeOptions(workCodeData.sort().map((option: string) => ({
          label: option,
          value: (option.match('(^\\d+)')?.[0] ?? ''),
        })));
      }
    } catch {
      setWorkCodeOptions([{ label: "", value: "" }]);
    }

    // Get Defect Failure Modes options
    await DataService.searchDataSourceData("DATA_DEFINITIONS", "21", "")
      .then((response) => {
        if (response.data) {
          setDefectFailureModesOptions(response.data.sort().map((option: MultiSelectOption) => ({ label: option, value: option })));
        }
      })
      .catch((e) => {
        setDefectFailureModesOptions([{ label: "", value: "" }]);
      });

    // Get Subcontractor Name options
    setLoadingMessage("Syncing Subcontractor Data from Blattner Mobile...");

    try {
      const targetDS = isEnterprise ? "BOOMI_SUBCONTRACTORS_ENTERPRISE" : "BOOMI_SUBCONTRACTORS";
      const searchResults = (await DataService.searchDataSourceDataWithQuery(targetDS, 1, subcontractorNameQuery)).data;
      setSubcontractorNameOptions(searchResults.sort().map((option: string) => ({ label: option, value: option })));
    } catch (e) {
      setSubcontractorNameOptions([{ label: "", value: "" }]);
    }

    // Get Supplier/Vendor Name options
    setLoadingMessage("Syncing Supplier/Vendor Data from Blattner Mobile...");
    try {
      const targetDS = isEnterprise ? "BOOMI_SUBCONTRACTORS_ENTERPRISE" : "BOOMI_SUBCONTRACTORS";
      const searchResults = (await DataService.searchDataSourceDataWithQuery(targetDS, 1, supplierVendorNameQuery)).data;
      setSupplierVendorNameOptions(searchResults.sort().map((option: string) => ({ label: option, value: option })));
    } catch (e) {
      setSupplierVendorNameOptions([{ label: "", value: "" }]);
    }
  };

  // Function to get all screen data for the selected NCR
  const getCurrentTask = async (id: number) => {
    try {
      setLoadingMessage("Getting NCR Task Data...");

      const taskResponse = await TaskService.getTaskDetails(id.toString());

      const options = (taskResponse.data.groupUsers as IEmployeeDetail[]).map((d: any) => ({
        "value": d.employeeEmail,
        "label": d.employeeName,
      }));

      // Convert NonconformanceType of "Process" or "Product" to Workmanship before setting task
      const taskData = taskResponse.data;
      const typeIndex = taskData.screen.screenFields.findIndex((x: any) => x.dataName === "NonconformanceType");
      if (typeIndex !== -1) {
        const type = taskData.screen.screenFields[typeIndex].value;
        if (type && ["Process", "Product"].includes(type)) {
          taskData.screen.screenFields[typeIndex].value = "Workmanship";
        }
      }

      setCurrentTask(taskData);
      setSelectOptionsReassign(options);

      // Get select field options
      await getSelectFieldOptions(taskData);

    } catch (error) {
      setError(true);
    } finally {
      setLoadingComplete(true);
    }
  };

  // Function to get all comments for the discussion component/tab
  const getTaskComments = async (id: number) => {
    await TaskService.getComments(id.toString())
      .then((response) => {
        setTaskComments(response.data);
        setTaskCommentsLoaded(true);
      }).catch((e) => {
        setTaskCommentsLoaded(true);
      });
  };

  // Function to get all history for the history tab
  const getTaskHistory = async (id: number) => {
    await TaskService.getHistory(id.toString())
      .then((response) => {
        setTaskHistory(response.data);
      });
  };

/* <------------------------> */
//#endregion
/* <------------------------> */


/* <------------------------> */
//#region SAVE, SUBMIT, CHECKIFCLAIMED FUNCTIONS
/* <------------------------> */

  // Function to save data to the database
  const saveTask = async (data: FormData) => {
    if (isMocked) {
      infoToast('You have clicked on the "Save & Close" button.  This has been disabled because you are currently not yourself.');
      setBackToTaskPage(true);
      return;
    }

    await TaskService.saveTask(data)
      .then((response) => {
        if (response.status === 200) {
          setBackToTaskPage(true);
        }
      }).catch((e) => {
        setLoadingComplete(true);
        setError(true);
      });
  };

  // Function to complete the task, save data to database, and create task for next step if there is any
  const submitTaskFn = async (data: FormData) => {
    if (isMocked) {
      infoToast('You have clicked on the "Submit" button.  This has been disabled because you are currently not yourself.');
      setBackToTaskPage(true);
      return;
    }

    await TaskService.submitTask(data)
      .then((response) => {
        if (response.status === 200) {
          TaskService.runTaskEvents(currentTask.id.toString());
          setBackToTaskPage(true);
        }
      }).catch((e) => {
        setLoadingComplete(true);
        setError(true);
      });
  };

  // Function to set WorkType based on WorkCode
  const setWorkType = async (workCode: string) => {
    const workTypeQuery = {
      query: [
        { column: 1, values: [currentTask.projectNumber] },
        { column: 2, values: [workCode] },
      ]
    };

    try {
      const targetDS = currentTask.screen.screenFields.find(x => x.dataName === 'JDESource')?.value === 'Enterprise' 
        ? "BOOMI_WORKCODES_ENTERPRISE" : "BOOMI_WORKCODES";
      const workCodeData = (await DataService.searchDataSourceDataWithQuery(targetDS, 8, workTypeQuery)).data;
      if (workCodeData) {
        setValue("WorkType", workCodeData[0]);
      }
    } catch (e) {
      console.log("An error occurred setting Work Type", e);
    }
  };

  // Checks if task is claimed. If claimed, shows the reassign, delete, and save button
  const checkIfTaskIsClaimed = () => {
    const claimed = currentTask.assignedTo;
    const userEmail = currentUser.email;

    if (!claimed && selectOptionsReassign.some(x => x.value.toLowerCase() === userEmail.toLowerCase())) {
      return (
        <div className="btn-group">
          <div id="RightAlignedFloatDiv">
            <ButtonType dataName="claimTask" color="blue" buttonText="Claim Task" eventHandler={() => claimTask(userEmail, currentTask.id)} />
          </div>
        </div>
      );
    } else if (!claimed && !selectOptionsReassign.some(x => x.value.toLowerCase() === userEmail.toLowerCase()) && overrideJobCodes.includes(userJobCode)) {
      return (
        <div id="RightAlignedFloatDiv">
          <ButtonType dataName="overrideClaim" color="blue" buttonText="Override Claim" eventHandler={() => claimTask(userEmail, currentTask.id)} />
        </div>
      );
    } else if (claimed && !(claimed.toLowerCase() === userEmail.toLowerCase()) && overrideJobCodes.includes(userJobCode)) {
      return (
        <div className="btn-group">
          <div id="RightAlignedFloatDiv">
            <ButtonType dataName="overrideClaim" color="blue" buttonText="Override Claim" eventHandler={() => claimTask(userEmail, currentTask.id)} />
          </div>
        </div>
      );
    } else if (claimed.toLowerCase() === userEmail.toLowerCase()) {
      return (
        <div className="btn-group">
          <ButtonType dataName="reassignTask" color="blue" buttonText="Reassign" eventHandler={onOpenReassignModal} />
          <ButtonType dataName="deleteTask" color="red" buttonText="Delete" eventHandler={onOpenDeleteModal} />
          <div id="RightAlignedFloatDiv">
            <ButtonType dataName="Save" color="blue" buttonText="Save & Close" type="submit" eventHandler={handleSetSubmitButton} ref={saveButtonRef} readOnly={formError} />
          </div>
        </div>
      );
    }
    return null;
  };

  // Determines if it was the save button that was clicked, or the submit button
  const handleSetSubmitButton = (event: any) => {
    const button = event.target.name;
    setSubmitButton(button);

    if (formRef.current) {
      if (button === "Submit") {
        formRef.current.className += " was-validated";
        formRef.current.noValidate = false;
      } else {
        formRef.current.noValidate = true;
      }
    }
  };

  // Saving all data to state. Check if save or submit and call the appropriate function
  const submitHandler = (event: any) => {
    event.preventDefault();

    const save = submitButton === "Save";
    const tabChange = submitButton === "";

    const formData = new FormData(event.target);

    if (tabChange) {
      setCurrentTask(prevTask => {
        const task = { ...prevTask };
        formData.forEach((value: FormDataEntryValue, key: string) => {
          if (!(value instanceof File)) {
            const existingIndex = task.screen.screenFields.findIndex(f => f.dataName === key);
            if (existingIndex >= 0) {
              task.screen.screenFields = [...task.screen.screenFields];
              task.screen.screenFields[existingIndex] = { ...task.screen.screenFields[existingIndex], value: value as string };
            } else {
              task.screen.screenFields = [...task.screen.screenFields, {
                dataName: key,
                value: value as string,
                type: FieldType.None,
                title: "",
                hintText: "",
                buttonText: "",
                buttonColor: "",
                headerColor: "",
                textColor: "",
                time: false,
                readOnly: false,
                required: false,
                options: []
              }];
            }
          }
        });
        return task;
      });
    } else if (save || event.target.checkValidity()) {
      let screenFields: IScreenFieldUpdate[] = [];
      let closureStatusValues: string[] = [];
      let newFormData = new FormData();

      formData.forEach((value: FormDataEntryValue, key: string) => {
        if (key === 'ClosureStatus') {
          closureStatusValues.push(value.toString());
        } else {
          newFormData.append(key, value);
        }
      });

      if (closureStatusValues.length > 0) {
        newFormData.append('ClosureStatus', closureStatusValues.join(', '));
      }

      newFormData.forEach((value: FormDataEntryValue, key: string) => {
        if (!(value instanceof File)) {
          screenFields.push({ dataName: key, value: value.toString() });
        }
      });

      screenFields.push({ dataName: "WorkType", value: getValue("WorkType") });

      setLoadingComplete(false);
      setLoadingMessage(save ? "Saving NCR Task..." : "Submitting NCR Task...");

      const data = new FormData();
      data.append("id", currentTask.id.toString());

      screenFields.forEach((field, i) => {
        data.append("screenFields[" + i + "].DataName", field.dataName);
        data.append("screenFields[" + i + "].Value", field.value);
      });

      if (fileUpload.files.length > 0) {
        fileUpload.files.forEach(file => {
          data.append("Files", file);
        });
        data.append("FileDataName", fileUpload.dataName);
        if (fileUpload.tableDataName) {
          data.append("TableDataName", fileUpload.tableDataName);
        }
        if (fileUpload.filenameDataName) {
          data.append("FilenameDataName", fileUpload.filenameDataName);
        }
      }

      if (save) {
        saveTask(data);
      } else {
        submitTaskFn(data);
      }
    }
  };

/* <------------------------> */
//#endregion
/* <------------------------> */


/* <------------------------> */
//#region MODAL FUNCTIONS
/* <------------------------> */

  // Function to set openDeleteModal variable to true when delete button clicked
  const onOpenDeleteModal = () => {
    setOpenDeleteModal(true);
  };

  // Function to set openDeleteModal variable to false when close or red X button clicked
  const onCloseDeleteModal = () => {
    setOpenDeleteModal(false);
    setDeleteReason("");
  };

  // Function to set openReassignModal variable to true when reassign button clicked
  const onOpenReassignModal = () => {
    setOpenReassignModal(true);
  };

  // Function to set openReassignModal variable to false when close or red X button clicked
  const onCloseReassignModal = () => {
    setOpenReassignModal(false);
    setSingleOptionsReassign("");
  };

  // Modal task handler
  const modalTaskHandler = (event: any) => {
    event.preventDefault();
    event.target.className += " was-validated";
  };

/* <------------------------> */
//#endregion
/* <------------------------> */


/* <------------------------> */
//#region HANDLERS
/* <------------------------> */

  // Function to update the currentTask state so that the choices field updates when a new value is selected
  const choicesValueChangeHandler = (dataName: string, event: any) => {
    setValue(dataName, event.target.value);

    if (dataName === "NonconformanceType") {
      handleTypeChange();
      setValue("ResponsibleParty", "");
      setValue("SearchBy", "");
    }
  };

  // Search field value change handler
  const searchFieldValueChangeHandler = (dataName: string, option: any) => {
    setValue(dataName, option);
    if (dataName === "WorkCode") {
      setWorkType(option);
    }
  };

  // Function to setState when Delete Reason text area value changes
  const handleChangeTextArea = (event: any) => {
    setDeleteReason(event.target.value);
  };

  // Function to add file to list
  const addFileHandler = (newFileUpload: IFileUpload) => {
    setFileUpload(prev => ({
      files: [...prev.files, ...newFileUpload.files],
      dataName: newFileUpload.dataName,
      tableDataName: newFileUpload.tableDataName,
      filenameDataName: newFileUpload.filenameDataName
    }));
  };

  // Function to remove file from list
  const removeFileHandler = (file: File) => {
    setFileUpload(prev => {
      const newFiles = [...prev.files];
      const index = newFiles.indexOf(file, 0);
      if (index > -1) {
        newFiles.splice(index, 1);
      }
      return {
        files: newFiles,
        dataName: prev.dataName,
        tableDataName: prev.tableDataName,
        filenameDataName: prev.filenameDataName
      };
    });
  };

  // Function to set tab state
  const handleTabChange = (selectedKey: any) => {
    const currentTab = tab;
    setSubmitButton("");
    
    if (currentTab === "NCR" && saveButtonRef.current) {
      saveButtonRef.current.click();
    }
    setTab(selectedKey);
  };

  // Function to add new comments
  const handleNewComment = (comments: ITaskComment[]) => {
    setTaskComments(comments);
  };

  // Function to set state for reassign user selected
  const onChangeReassignOption = (event: any) => {
    setSingleOptionsReassign(event ? event.target.value : "");
  };

  // Function to set singleOptionsReassign on the parent from the AD Search field
  const onADSearchChange = (value: string) => {
    setSingleOptionsReassign(value);
  };

  // Function to track qualityExpeditingStage child variable at the parent level
  const stageChange = (stage: Stage) => {
    setQualityExpeditingStage(stage);
  };

  // Function to track approval child variable at the parent level
  const approvalChange = (value: string) => {
    setApproval(value);
  };

  // Function to track reject to child variable at the parent level
  const rejectToChange = (value: string) => {
    setRejectTo(value);
  };

  // Function to track dcAcknowledgment child variable at the parent level
  const dcAcknowledgmentChange = (value: string) => {
    if (value) {
      setDcAcknowledgment(true);
    }
  };

  // Handle itemized cost change
  const handleItemizedCostChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name;
    const value = parseFloat(event.target.value) || 0;
    const roundedValue = FormatNumber(value, 2);
    setValue(name, roundedValue);

    const itemizedBlattnerCosts = [
      parseFloat(getValue(NCRDataConstants.INVESTIGATION_BLATTNER_COST)) || 0,
      parseFloat(getValue(NCRDataConstants.WORK_BLATTNER_COST)) || 0,
      parseFloat(getValue(NCRDataConstants.ADDITIONAL_BLATTNER_COST)) || 0,
      parseFloat(getValue(NCRDataConstants.REMOVAL_BLATTNER_COST)) || 0,
      parseFloat(getValue(NCRDataConstants.REINSTALLATION_BLATTNER_COST)) || 0,
      parseFloat(getValue(NCRDataConstants.OTHER_BLATTNER_COST)) || 0
    ];
    const itemizedNonBlattnerCosts = [
      parseFloat(getValue(NCRDataConstants.INVESTIGATION_NON_BLATTNER_COST)) || 0,
      parseFloat(getValue(NCRDataConstants.WORK_NON_BLATTNER_COST)) || 0,
      parseFloat(getValue(NCRDataConstants.ADDITIONAL_NON_BLATTNER_COST)) || 0,
      parseFloat(getValue(NCRDataConstants.REMOVAL_NON_BLATTNER_COST)) || 0,
      parseFloat(getValue(NCRDataConstants.REINSTALLATION_NON_BLATTNER_COST)) || 0,
      parseFloat(getValue(NCRDataConstants.OTHER_NON_BLATTNER_COST)) || 0
    ];

    const totalBlattnerCost = itemizedBlattnerCosts.reduce((partialSum, nextNum) => partialSum + nextNum);
    const totalNonBlattnerCost = itemizedNonBlattnerCosts.reduce((partialSum, nextNum) => partialSum + nextNum);
    const totalCombinedCost = totalBlattnerCost + totalNonBlattnerCost;

    setValue(NCRDataConstants.BLATTNER_TOTAL_COST, FormatNumber(totalBlattnerCost, 2));
    setValue(NCRDataConstants.NON_BLATTNER_TOTAL_COST, FormatNumber(totalNonBlattnerCost, 2));
    setValue(NCRDataConstants.COMBINED_TOTAL_COST, FormatNumber(totalCombinedCost, 2));
  };

  // Handle total cost change
  const handleTotalCostChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const name = event.target.name;
    const value = parseFloat(event.target.value) || 0;
    const roundedValue = FormatNumber(value, 2);
    setValue(name, roundedValue);

    const totalBlattnerCost = parseFloat(getValue(NCRDataConstants.BLATTNER_TOTAL_COST));
    const totalNonBlattnerCost = parseFloat(getValue(NCRDataConstants.NON_BLATTNER_TOTAL_COST));
    const totalCombinedCost = totalBlattnerCost + totalNonBlattnerCost;

    setValue(NCRDataConstants.BLATTNER_TOTAL_COST, FormatNumber(totalBlattnerCost, 2));
    setValue(NCRDataConstants.NON_BLATTNER_TOTAL_COST, FormatNumber(totalNonBlattnerCost, 2));
    setValue(NCRDataConstants.COMBINED_TOTAL_COST, FormatNumber(totalCombinedCost, 2));

    handleTierChange();
  };

/* <------------------------> */
//#endregion
/* <------------------------> */


/* <------------------------> */
//#region COMPONENT FUNCTIONS
/* <------------------------> */

  // Handle form errors
  const handleFormErrors = (errorState: boolean) => {
    setFormError(errorState);
  };

  // Function to Quality Expediting stage. Multiple stages can show this component
  const qualityExpediting = () => {
    return (
      <QualityExpediting
        task={currentTask}
        fileAddHandler={addFileHandler}
        fileRemoveHandler={removeFileHandler}
        stageChange={stageChange}
        files={fileUpload.files}
        onError={handleFormErrors}
      />
    );
  };

  // Function to determine which component to show depending on stage
  const additionalFields = () => {
    if (currentTask.assignedTo) {
      const ncrType = currentTask.ncrType;

      switch (currentTask.stage) {
        case Stage.EngineeringReview:
          return <EngineeringReview task={currentTask} />;
        case Stage.SupplyChainReview:
          return (
            <SupplyChainReview
              userData={currentUser}
              task={currentTask}
              fileAddHandler={addFileHandler}
              fileRemoveHandler={removeFileHandler}
              files={fileUpload.files}
            />
          );
        case Stage.SMPMApproval:
          return (
            <ApproveRejectField
              currentUser={currentUser}
              task={currentTask}
              dataName={NCRDataConstants.PM_SM_APPROVAL}
              title={NCRDataConstants.PM_SM_APPROVAL_TITLE}
              rejectionCommentsDataName={NCRDataConstants.PM_SM_REJECTION_COMMENTS}
              approverNameDataName={NCRDataConstants.PM_SM_APPROVAL_NAME}
              approveDateDataName={NCRDataConstants.PM_SM_APPROVAL_DATE}
              eventHandler={approvalChange}
              rejectToDataName={NCRDataConstants.PM_SM_REJECTION_REJECT_TO}
              rejectTo={ncrType.toUpperCase() === "ENGINEERING" ? ncrType : ""}
              rejectToEventHandler={rejectToChange}
            />
          );
        case Stage.DCAcknowledgment:
          return (
            <AcknowledgmentField
              task={currentTask}
              dataName={NCRDataConstants.DC_ACKNOWLEDGMENT}
              title={NCRDataConstants.DC_ACKNOWLEDGMENT_VALUE}
              header={NCRDataConstants.DC_ACKNOWLEDGMENT_HEADER}
              eventHandler={dcAcknowledgmentChange}
            />
          );
        case Stage.QualityApproval:
          return (
            <ApproveRejectField
              currentUser={currentUser}
              task={currentTask}
              dataName={NCRDataConstants.QUALITY_APPROVAL}
              title={NCRDataConstants.QUALITY_APPROVAL_TITLE}
              rejectionCommentsDataName={NCRDataConstants.QUALITY_REJECTION_COMMENTS}
              eventHandler={approvalChange}
              rejectToDataName={NCRDataConstants.QUALITY_REJECTION_REJECT_TO}
              rejectTo={ncrType.toUpperCase() === "ENGINEERING" ? ncrType : ""}
              rejectToEventHandler={rejectToChange}
            />
          );
        case Stage.QualityExpediting:
        case Stage.CorporateAnalysis:
        case Stage.Implementation:
          return qualityExpediting();
        default:
          return null;
      }
    }
    return null;
  };

  // Function to determine if the current user is the assignTo user
  const correctUser = (): boolean => {
    const claimed = currentTask.assignedTo;
    const userEmail = currentUser.email;
    return claimed.toLowerCase() === userEmail.toLowerCase();
  };

  // Function to check visibility of itemized costs
  const itemizedCostVisibility = (): boolean => {
    return getValue("CompleteNCRAtInitiation") === "Yes" || 
            currentTask.tier === 3 || 
            (currentTask.tier === 2 && getValue("NonconformanceType") === "Material") || 
            (currentTask.stage > Stage.QualityRefinement && parseFloat(getValue(NCRDataConstants.COMBINED_TOTAL_COST)) >= 25000);
  };

  // Function to render itemized cost section
  const itemizedCostSection = (task: ITask, headerTitle: string, headerHintText: string, blattnerCostDataName: string, nonBlattnerCostDataName: string, explainCostDataName: string) => {
    return (
      <SectionWrapper headerTitle={headerTitle} headerHintText={headerHintText}
        visible={itemizedCostVisibility()}
      >
        {/* Blattner Cost */}
        <NumberField dataName={blattnerCostDataName} title={"Blattner Cost"} value={getValue(blattnerCostDataName)}
          decimals={2}
          readonly={![Stage.SupplyChainReview, Stage.QualityRefinement].includes(task.stage)}
          required
          onchange={handleItemizedCostChange}
        />
        {/* Non-Blattner Cost */}
        <NumberField dataName={nonBlattnerCostDataName} title={"Non-Blattner Cost"} value={getValue(nonBlattnerCostDataName)}
          decimals={2}
          readonly={![Stage.SupplyChainReview, Stage.QualityRefinement].includes(task.stage)}
          required
          onchange={handleItemizedCostChange}
        />
        {/* Explanation of Cost */}
        <TextField dataName={explainCostDataName} title={"Explanation of Cost"} value={getValue(explainCostDataName)}
          readonly={![Stage.SupplyChainReview, Stage.QualityRefinement].includes(task.stage)}
          required
          visible={Number(getValue(blattnerCostDataName)) + Number(getValue(nonBlattnerCostDataName)) > 0}
        />
      </SectionWrapper>
    );
  };

  // Function to display photos/documents section
  const displayPhotosDocumentsSection = (textFieldTitle: string, textFieldDataName: string, photoDataName: string, documentDataName: string) => {
    // Create empty list of fields
    const fields = currentTask.screen.screenFields.slice(0, 0);
    
    // Add fields where data name includes dataName
    currentTask.screen.screenFields.forEach((field) => {
      if (field.dataName.includes(textFieldDataName)) {
        fields.push(field);
      }
    });

    // Return each photo/document by looping through the fields
    return (
      <SectionWrapper>
        {fields.map((field, i) => {
          return (
            <div key={field.dataName}>
              <TextField dataName={`${i+1}.${textFieldDataName}`} title={textFieldTitle} value={getValue(`${i+1}.${textFieldDataName}`)}
                readonly
                visible={getValue(`${i+1}.${textFieldDataName}`) !== ""}
              />
              <MediaField dataName={`${i+1}.${photoDataName}`} title={""} value={getValue(`${i+1}.${photoDataName}`)}
                visible={getValue(`${i+1}.${photoDataName}`) !== ""}
              />
              <FileField title={""} value={getValue(`${i+1}.${documentDataName}`)} 
                visible={getValue(`${i+1}.${documentDataName}`) !== ""}
              />
            </div>
          );
        })}
      </SectionWrapper>
    );
  };

  // Function to check if task is completed or assigned
  const checkIfCompletedOrAssigned = () => {
    // Display that the task is already complete
    if (currentTask.completed) {
      return alertComponent(
        "This task has already been completed.",
        "info",
        "Go Back"
      );
    }
    // Display that the task is claimed by another user
    else if (!correctUser() && currentTask.assignedTo !== "") {
      return alertComponent(
        `This task has been claimed by ${currentTask.assignedToName}. You may view the task but you cannot edit it.`, 
        "warning",
        "Go Back"
      );
    }
    // Display that the task is not claimable by the current user (not claimed and user not in the groupUsers list)
    else if (currentTask.assignedTo === "" && !currentTask.groupUsers?.find(x => x.employeeEmail === currentUser.email)) {
      return alertComponent(
        `This task cannot be claimed because you do not have the appropriate permissions. You may view the task but you cannot edit it.`, 
        "warning",
        "Go Back"
      );
    }
    else {
      return null;
    }
  };

  // Alert component helper
  const alertComponent = (value: string, variant: string, buttonText?: string) => {
    return (
      <div>
        <br/>
        <Alert variant={variant}>
          <input type="text" className="input-hidden" autoFocus={true}/>
          {value}
          {buttonText ? (
            <div>
              <hr />
              <div className="d-flex justify-content-end">
                <Button onClick={() => setBackToTaskPage(true)} variant={variant}>
                  {buttonText}
                </Button>
              </div>
            </div>
          ) : null}
        </Alert>
      </div>
    );
  };

/* <------------------------> */
//#endregion
/* <------------------------> */

  // Effect to load initial data (replaces componentDidMount)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (taskId) {
      getCurrentTask(parseInt(taskId));
      getTaskComments(parseInt(taskId));
      getTaskHistory(parseInt(taskId));
    }
  }, [taskId]);

  // Effect to handle navigation when backToTaskPage becomes true
  useEffect(() => {
    if (backToTaskPage) {
      history.push('/tasks');
    }
  }, [backToTaskPage, history]);

/* <------------------------> */
//#region RENDER LOGIC
/* <------------------------> */

  // Determine submit button visibility and state
  let submitButtonElement = null;
  if (currentTask.id != null && correctUser()) {
    let isReadOnly = false;
    
    // SM/PM Approval & Quality Approval
    if (currentTask.stage === Stage.SMPMApproval || currentTask.stage === Stage.QualityApproval) {
      isReadOnly = !approval || 
          (approval === NCRDataConstants.APPROVAL_REJECT && ((currentTask.ncrType.toUpperCase() === "WORKMANSHIP" ||
            (currentTask.ncrType.toUpperCase() === "ENGINEERING" && rejectTo === NCRDataConstants.APPROVAL_REJECT_ASSIGNEE)) &&           
            (!currentTask.rejectionUsers || currentTask.rejectionUsers.length === 0)));
    }

    // DC Acknowledgment
    if (currentTask.stage === Stage.DCAcknowledgment) {
      isReadOnly = !dcAcknowledgment;
    }

    submitButtonElement = (
      <div className="ButtonDiv">
        <br />
        <ButtonType
          dataName="Submit"
          color="blue"
          buttonText="Submit"
          type="submit"
          size="lg"
          readOnly={isReadOnly || !loadingComplete || formError}
          eventHandler={handleSetSubmitButton}
        />
      </div>
    );
  }
    
/* <------------------------> */
//#endregion
/* <------------------------> */

  return (

/* <------------------------> */
//#region RETURN
/* <------------------------> */
    <div className='row w-75'>
      {
        <div>
          <LoadingWrapper showLoading={!loadingComplete} loadingMessage={loadingMessage} windSpinner windSpinnerScale={3}>
          {checkIfCompletedOrAssigned()}
          {error ? alertComponent("An error occurred.  Please try again or contact IT Support.", "danger") : null }
          <Card>
            <Card.Header>
              <Nav fill variant="tabs" activeKey={tab} onSelect={handleTabChange}>
                <Nav.Item>
                  <Nav.Link eventKey="NCR">NCR</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="Discussion" disabled={taskCommentsLoaded ? false : true}>Discussion {taskComments.length > 0 ? "(" + taskComments.length.toString() + ")" : null}</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="History">History</Nav.Link>
                </Nav.Item>
              </Nav>
            </Card.Header>
            <Card.Body>
              {tab === "NCR" ? 
              <div>
              <div style={{textAlign: "center", marginTop: "1000"}}>

              <Modal show={openDeleteModal} onHide={onCloseDeleteModal} size="lg">
                <form className="needs-validation" onSubmit={modalTaskHandler} noValidate>
                  <Modal.Header closeButton>
                      <Modal.Title>Are you sure you want to delete this NCR?</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                      <div className="form-group">
                        <TextArea dataName="Messaging" value={deleteReason} title="Reason for deleting NCR" onchange={handleChangeTextArea} required={true} />
                      </div>
                    </Modal.Body>
                    <Modal.Footer>
                      <br />
                      <ButtonType dataName="submit" color="blue" size="lg" type="submit" buttonText="Yes" eventHandler={() => deleteTask(currentTask.id, deleteReason)} />
                      <ButtonType dataName="submit" color="blue" size="lg" buttonText="No" eventHandler={onCloseDeleteModal} />
                      <br />
                    </Modal.Footer>
                  </form>
                </Modal>

              <Modal show={openReassignModal} onHide={onCloseReassignModal}>
                <form className="needs-validation" onSubmit={modalTaskHandler} noValidate>
                  <Modal.Header closeButton>
                    <Modal.Title>Reassign Task To:</Modal.Title>
                  </Modal.Header>
                  <Modal.Body>
                    <div className="form-group">
                      {currentTask.stage > Stage.QualityExpediting || qualityExpeditingStage > Stage.QualityExpediting ?
                        <ADSearchField dataName="reassignTo" title="" required={true} onchange={onADSearchChange} optionValue="Email" />
                        :
                        <SelectField onchange={onChangeReassignOption} required={true}
                            options={selectOptionsReassign} dataName="reassignTo" value={singleOptionsReassign} title="" />
                      }
                    </div>
                  </Modal.Body>
                  <Modal.Footer>
                  <br />
                    <ButtonType dataName="submit" color="blue" size="lg" type="submit" buttonText="Reassign Task" eventHandler={() => claimTask(singleOptionsReassign, currentTask.id)} />
                    <ButtonType dataName="submit" color="blue" size="lg" buttonText="Cancel" eventHandler={onCloseReassignModal} />
                    <br />
                  </Modal.Footer>
                </form>
              </Modal>

            </div>
            <form className="needs-validation" onSubmit={submitHandler} ref={formRef} noValidate>
              <div>
                {checkIfTaskIsClaimed()}
              </div>
              <h1 className="header mt-5 text-center">{currentTask.screen.name}</h1>
              <div className="mt-4">
              <div><p>Current Stage is <i>{StageLabel[currentTask.stage]}</i></p></div>

              {/* Disable all fields if not assigned to logged in user */}
              <fieldset disabled={currentTask.assignedTo !== currentUser.email}>
                {/* NCR BASE DETAILS */}
                <SectionWrapper headerTitle={`${currentTask.projectNumber} - ${currentTask.projectName}`}>
                  {/* Rejection Comments */}
                  <TextField dataName={"PMSM_CommentsRejection"} title={"PM/SM Rejection Comments"} value={getValue("PMSM_CommentsRejection")}
                    readonly
                    visible={getValue("PMSM_CommentsRejection") !== ""}
                  />
                  <TextField dataName={"Quality_CommentsRejection"} title={"Quality Rejection Comments"} value={getValue("Quality_CommentsRejection")}
                    readonly
                    visible={getValue("Quality_CommentsRejection") !== ""}
                  />
                  {/* Detected By */}
                  <TextField dataName={"DetectedBy"} title={"Detected By"} value={getValue("DetectedBy")}
                    readonly
                  />
                  {/* Initial Cost Estimate */}
                  <NumberField dataName={"CostEstimate"} title={"Initial Cost Estimate"} value={getValue("CostEstimate")}
                    readonly
                  />
                  {/* Assign To */}
                  <TextField dataName={"Assignee"} title={"Assign To"} value={getValue("Assignee")}
                    readonly
                    visible={getValue("Assignee") !== ""}
                  />
                  {/* Nonconformance Discovery Date */}
                  <DateField dataName={"NcrDate"} title={"Nonconformance Discovery Date"} value={getValue("NcrDate")}
                    readonly={currentTask.stage !== Stage.QualityRefinement}
                    required
                    onchange={(val) => setValue("NcrDate", val)}
                  />
                  {/* Target Completion Date */}
                  <DateField dataName={"TargetDate"} title={"Target Completion Date"} value={getValue("TargetDate")}
                    readonly={currentTask.stage !== Stage.QualityRefinement}
                    required
                    visible={currentTask.stage >= Stage.QualityRefinement && currentTask.tier === 3}
                    onchange={(val) => setValue("TargetDate", val)}
                  />
                  {/* Work Code - match only the work code as it comes from the form entry */}
                  <SearchField dataName={"WorkCode"} title={"Work Code"} value={getValue("WorkCode").match('(^\\d+)')?.[0] ?? ''}
                    options={workCodeOptions}
                    readonly={currentTask.stage !== Stage.QualityRefinement}
                    required
                    maxDisplayOptions={100}
                    findValueIncludes // So that old values (just a number) are still found
                    onchange={(e) => searchFieldValueChangeHandler("WorkCode", e)}
                  />
                  {/* Type of Nonconformance */}
                  <SelectField dataName={"NonconformanceType"} title={"Type of Nonconformance"} value={getValue("NonconformanceType")}
                    options={[
                      {value: "Material"},
                      {value: "Workmanship"}
                    ]}
                    readonly={currentTask.stage !== Stage.QualityRefinement}
                    required
                    onchange={(e) => choicesValueChangeHandler("NonconformanceType", e)}
                  />
                  {/* Responsible Party */}
                  <SelectField dataName={"ResponsibleParty"} title={"Responsible Party"} value={getValue("ResponsibleParty")}
                    options={getValue("NonconformanceType") === "Material" ? [
                      {value: "Owner Supplier/Vendor"},
                      {value: "Supplier/Vendor"}
                    ]
                    : [
                      {value: "Blattner"},
                      {value: "Engineering"},
                      {value: "Subcontractor"}
                    ]}
                    readonly={currentTask.stage !== Stage.QualityRefinement}
                    required
                    onchange={(e) => choicesValueChangeHandler("ResponsibleParty", e)}
                  />
                  {/* Search By */}
                  <SelectField dataName={"SearchBy"} title={"Search By"} value={getValue("SearchBy")}
                    options={getValue("NonconformanceType") === "Material" ? [
                      {value: "Supplier/Vendor Name"},
                      {value: "Other Supplier/Vendor Name"}
                    ]
                    : [
                      {value: "Subcontractor Name"},
                      {value: "Other Subcontractor Name"}
                    ]}
                    visible={(getValue("ResponsibleParty").includes("Supplier/Vendor") || getValue("ResponsibleParty") === "Subcontractor") && currentTask.stage === Stage.QualityRefinement}
                    required
                    onchange={(e) => choicesValueChangeHandler("SearchBy", e)}
                  />
                  {/* Subcontractor Name */}
                  <SearchField dataName={"SubcontractorName"} title={"Subcontractor Name"} value={getValue("SubcontractorName")}
                    options={subcontractorNameOptions}
                    visible={(getValue("ResponsibleParty") === "Subcontractor" && getValue("SearchBy") === "Subcontractor Name") ||
                    (currentTask.stage < Stage.QualityRefinement && getValue("SubcontractorName") !== "")}
                    readonly={currentTask.stage !== Stage.QualityRefinement}
                    required
                    maxDisplayOptions={100}
                    onchange={(e) => searchFieldValueChangeHandler("SubcontractorName", e)}
                  />
                  <TextField dataName={"OtherSubcontractorName"} title={"Other Subcontractor Name"} value={getValue("OtherSubcontractorName")}
                    readonly={currentTask.stage !== Stage.QualityRefinement}
                    required
                    visible={(getValue("ResponsibleParty") === "Subcontractor" && getValue("SearchBy") === "Other Subcontractor Name") ||
                    (currentTask.stage < Stage.QualityRefinement && getValue("OtherSubcontractorName") !== "")}
                  />
                  {/* Supplier/Vendor Name */}
                  <SearchField dataName={"SupplierVendorName"} title={"Supplier/Vendor Name"} value={getValue("SupplierVendorName")}
                    options={supplierVendorNameOptions}
                    visible={(getValue("ResponsibleParty").includes("Supplier/Vendor") && getValue("SearchBy") === "Supplier/Vendor Name") ||
                      (currentTask.stage < Stage.QualityRefinement && getValue("SupplierVendorName") !== "")}
                    readonly={currentTask.stage !== Stage.QualityRefinement}
                    required
                    maxDisplayOptions={100}
                    onchange={(e) => searchFieldValueChangeHandler("SupplierVendorName", e)}
                  />
                  <TextField dataName={"OtherSupplierVendorName"} title={"Other Supplier/Vendor Name"} value={getValue("OtherSupplierVendorName")}
                    readonly={currentTask.stage !== Stage.QualityRefinement}
                    required
                    visible={(getValue("ResponsibleParty").includes("Supplier/Vendor") && getValue("SearchBy") === "Other Supplier/Vendor Name") ||
                    (currentTask.stage < Stage.QualityRefinement && getValue("OtherSupplierVendorName") !== "")}
                  />
                  {/* Defect Failure Modes */}
                  <SelectField dataName={"DefectFailureModes"} title={"Defect Failure Modes"} value={getValue("DefectFailureModes")}
                    options={defectFailureModesOptions}
                    hintText={"The way a material/product has failed to meet specifications, prints or contractual obligations. Failure modes are errors or nonconformances and can be potential or actual."}
                    readonly={![Stage.SupplyChainReview, Stage.QualityRefinement].includes(currentTask.stage)}
                    required
                    visible={getValue("NonconformanceType") === "Material"}
                    onchange={(e) => choicesValueChangeHandler("DefectFailureModes", e)}
                  />
                  {/* Document */}
                  {displayPhotosDocumentsSection("Document Title", "DocumentTitle", "", "ChooseFile")}
                  {/* Nonconformance Description */}
                  <TextArea dataName={"NonconformanceDescription"} title={"Nonconformance Description"} value={getValue("NonconformanceDescription")}
                    readonly={currentTask.stage !== Stage.QualityRefinement}
                    required
                    rows={3}
                  />
                  {/* Nonconformance Location Details */}
                  <TextArea dataName={"NonconformanceLocation"} title={"Nonconformance Location Details"} value={getValue("NonconformanceLocation")}
                    readonly={currentTask.stage !== Stage.QualityRefinement}
                    required
                    rows={3}
                  />
                  {/* Mechanical or Substantial Completion Will Be Impacted */}
                  <SelectField dataName={"CompletionImpactedYN"} title={"Mechanical or Substantial Completion Will Be Impacted"} value={getValue("CompletionImpactedYN")}
                    options={[{value: "Yes"}, {value: "No"}]} 
                    readonly
                    onchange={(e) => choicesValueChangeHandler("CompletionImpactedYN", e)}
                  />
                  {/* Confirmed With Your Direct Supervisor */}
                  <SelectField dataName={"DoubleCheckWithSM"} title={"Confirmed With Your Direct Supervisor"} value={getValue("DoubleCheckWithSM")}
                    options={[{value: "Yes"}, {value: "No"}]} 
                    readonly
                    onchange={(e) => choicesValueChangeHandler("DoubleCheckWithSM", e)}
                  />
                  {/* Photos/Documents */}
                  {displayPhotosDocumentsSection("Photo/Document Title", "TitleName", "UploadPhoto", "UploadDocument")}
                  {/* Complete Full NCR at Initiation */}
                  <SelectField dataName={"CompleteNCRAtInitiation"} title={"Complete Full NCR at Initiation"} value={getValue("CompleteNCRAtInitiation")}
                    options={[{value: "Yes"}, {value: "No"}]} 
                    readonly
                    visible={getValue("CompleteNCRAtInitiation") !== ""}
                    onchange={(e) => choicesValueChangeHandler("CompleteNCRAtInitiation", e)}
                  />
                </SectionWrapper>

                {/* RESOLUTION, CAUSE, & ACTION */}
                <SectionWrapper headerTitle="Resolution, Cause, & Action"
                  visible={currentTask.stage > Stage.EngineeringReview || getValue("CompleteNCRAtInitiation") === "Yes" || currentTask.tier === 3}
                >
                  {/* Resolution Proposal */}
                  <SelectField dataName={"ResolutionProposal"} title={"Resolution Proposal"} value={getValue("ResolutionProposal")}
                    options={[
                      {value: "Redesign"},
                      {value: "Return to Supplier/Replace"},
                      {value: "Rework/Repair"},
                      {value: "Use in AS IS Condition"}
                    ]}
                    allowSelectOption
                    readonly={currentTask.stage !== Stage.QualityRefinement}
                    required={getValue("CompleteNCRAtInitiation") === "Yes" || currentTask.tier === 3}
                    visible={currentTask.stage >= Stage.QualityRefinement || getValue("ResolutionProposal") !== ""}
                    onchange={(e) => choicesValueChangeHandler("ResolutionProposal", e)}
                  />
                  {/* Corrective Action Details */}
                  <TextArea dataName={"CorrectiveAction"} title={"Corrective Action Details"} value={getValue("CorrectiveAction")}
                    hintText={
                      currentTask.stage === Stage.SupplyChainReview || parseFloat(getValue(NCRDataConstants.COMBINED_TOTAL_COST)) >= 25000 ?
                        "Describe, in detail, how Resolution Proposal will be achieved."
                        : currentTask.stage === Stage.QualityRefinement ?
                          "When not applicable, this can be left blank."
                          : ""
                    }
                    readonly={![Stage.SupplyChainReview, Stage.QualityRefinement].includes(currentTask.stage)}
                    required={currentTask.stage === Stage.SupplyChainReview || parseFloat(getValue(NCRDataConstants.COMBINED_TOTAL_COST)) >= 25000}
                    visible={currentTask.stage < Stage.QualityExpediting || getValue("CorrectiveAction") !== ""}
                    rows={3}
                  />
                  {/* Root Cause Analysis */}
                  <TextArea dataName={"CauseAnalysis"} title={"Root Cause Analysis"} value={getValue("CauseAnalysis")}
                    hintText={
                      parseFloat(getValue(NCRDataConstants.COMBINED_TOTAL_COST)) < 25000 ?
                        currentTask.stage === Stage.QualityRefinement ?
                        "When not applicable, this can be left blank."
                        : ""
                      : ""
                    }
                    readonly={![Stage.SupplyChainReview, Stage.QualityRefinement].includes(currentTask.stage)}
                    required={currentTask.stage === Stage.SupplyChainReview || parseFloat(getValue(NCRDataConstants.COMBINED_TOTAL_COST)) >= 25000}
                    visible={currentTask.stage < Stage.QualityExpediting || getValue("CauseAnalysis") !== ""}
                    rows={3}
                  />
                  {/* Preventative Action Details */}
                  <TextArea dataName={"PreventativeAction"} title={"Preventative Action Details"} value={getValue("PreventativeAction")}
                    hintText={
                      currentTask.stage === Stage.SupplyChainReview || parseFloat(getValue(NCRDataConstants.COMBINED_TOTAL_COST)) >= 25000 ?
                        "The Preventative Action(s) must address the Root Cause."
                        : currentTask.stage === Stage.QualityRefinement ?
                          "When not applicable, this can be left blank."
                          : ""
                    }
                    readonly={![Stage.SupplyChainReview, Stage.QualityRefinement].includes(currentTask.stage)}
                    required={currentTask.stage === Stage.SupplyChainReview || parseFloat(getValue(NCRDataConstants.COMBINED_TOTAL_COST)) >= 25000}
                    visible={currentTask.stage < Stage.QualityExpediting || getValue("PreventativeAction") !== ""}
                    rows={3}
                  />
                  {/* Quality Exposure */}
                  <SelectField dataName={"QualityRiskTopics"} title={"Quality Exposure"} value={getValue("QualityRiskTopics")}
                    options={[
                      {value: "Labeling/Marking"},
                      {value: "Material Condition/Manufacture"},
                      {value: "Material Handling/Storage"},
                      {value: "Measure/Align"},
                      {value: "Pre/Post Job Inspection"},
                      {value: "Reference/Procedure"},
                      {value: "Tool/Equipment"}
                    ]} 
                    readonly={currentTask.stage !== Stage.QualityRefinement}
                    required
                    visible={currentTask.stage >= Stage.QualityRefinement}
                    onchange={(e) => choicesValueChangeHandler("QualityRiskTopics", e)}
                  />
                </SectionWrapper>

                {/* NONCONFORMANCE INVESTIGATION COST */}
                {itemizedCostSection(
                  currentTask,
                  "Nonconformance Investigation Cost",
                  "Includes all team members involved",
                  NCRDataConstants.INVESTIGATION_BLATTNER_COST,
                  NCRDataConstants.INVESTIGATION_NON_BLATTNER_COST,
                  NCRDataConstants.INVESTIGATION_COST_EXPLANATION
                )}
                
                {/* WORK STOPPAGE COST */}
                {itemizedCostSection(
                  currentTask,
                  "Work Stoppage Cost",
                  "Include cost related to work stoppage",
                  NCRDataConstants.WORK_BLATTNER_COST,
                  NCRDataConstants.WORK_NON_BLATTNER_COST,
                  NCRDataConstants.WORK_COST_EXPLANATION
                )}

                {/* ADDITIONAL MEETING COST */}
                {itemizedCostSection(
                  currentTask,
                  "Additional Meeting Cost",
                  "Include meeting costs related to nonconformance",
                  NCRDataConstants.ADDITIONAL_BLATTNER_COST,
                  NCRDataConstants.ADDITIONAL_NON_BLATTNER_COST,
                  NCRDataConstants.ADDITIONAL_COST_EXPLANATION
                )}
                
                {/* REMOVAL/DEMOLITION COST */}
                {itemizedCostSection(
                  currentTask,
                  "Removal/Demolition Cost",
                  "Include costs for labor, equipment, materials, engineering, etc",
                  NCRDataConstants.REMOVAL_BLATTNER_COST,
                  NCRDataConstants.REMOVAL_NON_BLATTNER_COST,
                  NCRDataConstants.REMOVAL_COST_EXPLANATION
                )}

                {/* REINSTALLATION COST */}
                {itemizedCostSection(
                  currentTask,
                  "Reinstallation Cost",
                  "Include costs for labor, equipment, materials, engineering, etc.",
                  NCRDataConstants.REINSTALLATION_BLATTNER_COST,
                  NCRDataConstants.REINSTALLATION_NON_BLATTNER_COST,
                  NCRDataConstants.REINSTALLATION_COST_EXPLANATION
                )}

                {/* OTHER/ADDITIONAL COST */}
                {itemizedCostSection(
                  currentTask,
                  "Other/Additional Cost",
                  "Include any additional costs not defined above",
                  NCRDataConstants.OTHER_BLATTNER_COST,
                  NCRDataConstants.OTHER_NON_BLATTNER_COST,
                  NCRDataConstants.OTHER_COST_EXPLANATION
                )}

                {/* TOTAL COSTS */}
                <SectionWrapper headerTitle="Total Cost(s)"
                  visible={currentTask.stage > Stage.EngineeringReview || getValue("CompleteNCRAtInitiation") === "Yes" || currentTask.tier === 3}
                >
                  {/* Blattner Total Cost */}
                  <NumberField dataName={NCRDataConstants.BLATTNER_TOTAL_COST} title={"Blattner Total Cost"} value={getValue(NCRDataConstants.BLATTNER_TOTAL_COST)}
                    decimals={2}
                    readonly={itemizedCostVisibility() || currentTask.stage > Stage.QualityRefinement}
                    required
                    onchange={handleTotalCostChange}
                  />
                  {/* Non-Blattner Total Cost */}
                  <NumberField dataName={NCRDataConstants.NON_BLATTNER_TOTAL_COST} title={"Non-Blattner Total Cost"} value={getValue(NCRDataConstants.NON_BLATTNER_TOTAL_COST)}
                    decimals={2}
                    readonly={itemizedCostVisibility() || currentTask.stage > Stage.QualityRefinement}
                    required
                    onchange={handleTotalCostChange}
                  />
                  {/* Combined Total Cost */}
                  <NumberField dataName={NCRDataConstants.COMBINED_TOTAL_COST} title={"Combined Total Cost"} value={getValue(NCRDataConstants.COMBINED_TOTAL_COST)}
                    decimals={2}
                    readonly
                  />
                </SectionWrapper>

                {/* PROJECT TEAM MEMBERS */}
                <SectionWrapper headerTitle="Project Team Members">
                  {/* Foreperson/Leadperson */}
                  <TextField dataName={"Foreperson"} title={"Primary Foreperson/Leadperson"} value={getValue("Foreperson")}
                    readonly
                    visible={getValue("Foreperson") !== ""}
                  />
                  <TextField dataName={"Foreperson2"} title={"Secondary Foreperson/Leadperson"} value={getValue("Foreperson2")}
                    readonly
                    visible={getValue("Foreperson2") !== ""}
                  />
                  {/* Trade Superintendent */}
                  <TextField dataName={"TradeSup"} title={"Primary Trade Superintendent"} value={getValue("TradeSup")}
                    readonly
                    visible={getValue("TradeSup") !== ""}
                  />
                  <TextField dataName={"TradeSup2"} title={"Secondary Trade Superintendent"} value={getValue("TradeSup2")}
                    readonly
                    visible={getValue("TradeSup2") !== ""}
                  />
                </SectionWrapper>

                {/* ADDITIONAL ITEMS (DISPLAY ONLY) */}
                <SectionWrapper
                  visible={currentTask.stage > Stage.SupplyChainReview}
                >
                  {displayPhotosDocumentsSection("Photo/Document Title", "AdditionalPhotoTitle", "", "AdditionalPhotoURL")}
                </SectionWrapper>

                {/* ENGINEERING (DISPLAY ONLY) */}
                <SectionWrapper headerTitle="Engineering"
                  visible={currentTask.stage > Stage.EngineeringReview && getValue("EngineerPerformedBy") !== ""}
                >
                  <TextField dataName={"EngineerComments"} title={"Comments"} value={getValue("EngineerComments")}
                    readonly
                  />
                  <TextField dataName={"EngineerPerformedBy"} title={"Engineering Performed By"} value={getValue("EngineerPerformedBy")}
                    readonly
                  />
                  <TextField dataName={"ExtEngineerFirm"} title={"External Engineering Firm"} value={getValue("ExtEngineerFirm")}
                    readonly
                    visible={getValue("ExtEngineerFirm") !== ""}
                  />
                </SectionWrapper>

                {/* PM/SM APPROVAL (DISPLAY ONLY) */}
                <SectionWrapper headerTitle="PM/SM Approval"
                  visible={currentTask.stage > Stage.SMPMApproval && getValue("PMSMApproval") !== ""}
                >
                  <TextField dataName={"PMSMApproval"} title={"PM/SM Approval"} value={getValue("PMSMApproval")}
                    readonly
                  />
                </SectionWrapper>

                {/* DC ACKNOWLEDGEMENT (DISPLAY ONLY) */}
                <SectionWrapper headerTitle="DC Acknowledgment"
                  visible={currentTask.stage > Stage.DCAcknowledgment && getValue("DCAcknowledgment") !== ""}
                >
                  <TextField dataName={"DCAcknowledgment"} title={"DC Acknowledgment"} value={getValue("DCAcknowledgment")}
                    readonly
                  />
                </SectionWrapper>

                {/* QUALITY APPROVAL (DISPLAY ONLY) */}
                <SectionWrapper headerTitle="Quality Approval"
                  visible={currentTask.stage > Stage.QualityApproval && getValue("QualityApproval") !== ""}
                >
                  <TextField dataName={"QualityApproval"} title={"Quality Approval"} value={getValue("QualityApproval")}
                    readonly
                  />
                </SectionWrapper>

                {additionalFields()}
                 
                {submitButtonElement}
              </fieldset>
              </div>
              
            </form>
            </div>
              : null}
            {tab === "Discussion" ?
                <TaskDiscussion userData={currentUser} isMocked={isMocked} taskID={taskId ? parseInt(taskId) : 0} preloadedComments={taskComments} addEvent={handleNewComment} />
                : null}
            {tab === "History" ? 
                <TaskHistory preloadedHistorys={taskHistory} />
                : null }
            </Card.Body>
          </Card>
          </LoadingWrapper>
        </div>
      }
    </div>


/* <------------------------> */
//#endregion
/* <------------------------> */

  );
};

export default TaskDetailsPage;